// VOY-485 (rebuild) — Loyalty programs (Settings tab).
//
// DECLARED INPUT. The existing `LoyaltyProgramsModern` component (used by
// the live profile via a Dialog wrapper) already implements add / edit /
// remove / drag-reorder priorities + grouping by program type. We reuse
// it AS-IS inside a Dialog, exactly like the live `LoyaltyCard` does, so
// the editing surface stays identical to what users have today.
//
// The card itself shows a v6 summary: count by type + "+ Add" / "Manage"
// CTAs that open the dialog.

import { useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LoyaltyProgramsModern } from '@/components/profile/loyalty-programs-modern'
import { getUserLoyaltyMemberships } from '@/lib/profile'
import type { UserLoyaltyMembership } from '@/types/profile'

export interface LoyaltyProgramsSectionProps {
  userId: string
}

export function LoyaltyProgramsSection({ userId }: LoyaltyProgramsSectionProps) {
  const [memberships, setMemberships] = useState<UserLoyaltyMembership[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  async function load() {
    setLoading(true)
    try {
      setMemberships(await getUserLoyaltyMemberships(userId))
    } catch (err) {
      console.error('[LoyaltyProgramsSection] load failed:', err)
    } finally {
      setLoading(false)
    }
  }

  function onDialogChange(open: boolean) {
    setIsOpen(open)
    if (!open) void load()  // refresh when the dialog closes — user may have added/removed
  }

  // Counts by program type for the inline summary
  const byType = memberships.reduce<Record<string, number>>((acc, m) => {
    const t = m.program?.program_type || 'other'
    acc[t] = (acc[t] || 0) + 1
    return acc
  }, {})
  const summary = Object.entries(byType)
    .map(([t, n]) => `${n} ${t.replace('_', ' ')}`)
    .join(' · ')

  return (
    <>
      <section className="card" aria-labelledby="loyalty-h">
        <div className="card-h">
          <h3 id="loyalty-h">Loyalty programs</h3>
          <button type="button" className="btn-ghost" style={{ padding: '5px 12px', fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setIsOpen(true)}>
            <Plus aria-hidden="true" style={{ width: 12, height: 12 }} />
            {memberships.length === 0 ? 'Add' : 'Manage'}
          </button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--v6-fg-muted)', fontSize: 13, margin: 0 }}>Loading…</p>
        ) : memberships.length === 0 ? (
          <p style={{ color: 'var(--v6-fg-muted)', fontSize: 13, margin: 0 }}>
            Link airline + hotel memberships so Voy can prioritize them in search.
          </p>
        ) : (
          <>
            <p style={{ color: 'var(--v6-fg-muted)', fontSize: 12.5, margin: '0 0 10px' }}>
              {summary}
            </p>
            <ul className="recent-list">
              {memberships.slice(0, 4).map((m) => (
                <li key={m.id}>
                  <div className="recent-row" style={{ cursor: 'default' }}>
                    <span className="recent-summary">{m.program?.program_name || 'Program'}</span>
                    <span className="recent-date">{m.member_id || '—'}{m.tier ? ` · ${m.tier}` : ''}</span>
                  </div>
                </li>
              ))}
              {memberships.length > 4 && (
                <li style={{ color: 'var(--v6-fg-muted)', fontSize: 12, padding: '4px 0 0 8px' }}>
                  +{memberships.length - 4} more
                </li>
              )}
            </ul>
          </>
        )}
      </section>

      <Dialog open={isOpen} onOpenChange={onDialogChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Loyalty programs</DialogTitle>
            <DialogDescription>
              Add and manage your airline + hotel memberships. Drag to reorder by priority.
            </DialogDescription>
          </DialogHeader>
          <LoyaltyProgramsModern className="w-full" />
        </DialogContent>
      </Dialog>
    </>
  )
}
